import { CourseDataApi } from "./api/course-data.api";
import { AnalyticsEventPayload } from "@features/shared/entities/features-api";
import { uuidv7 } from "uuidv7";
import { detectInitialLanguage } from "i18n";
import {
    initPostHog,
    posthogCapture,
    posthogIdentify,
    posthogSetPersonProperties,
    posthogReset,
    setPostHogExposureMirror
} from "./posthog";
import { toVariantValue } from "@features/flags-provider";
import { getAdConsent } from "@utils/cookieConsent";

/** Events that carry the CookieYes ad consent (consent_ad_user_data / consent_ad_personalization). */
const AD_CONSENT_EVENTS = new Set([
    "user_email_collected",
    "products_screen_presented",
    "purchase_completed_success"
]);

// Event posts are fire-and-forget, but a page load fires twenty of them at once and, over HTTP/1.1,
// they take every connection the data requests need. Two in flight at a time keeps order and
// delivers them all, just a little later.
const MAX_EVENT_POSTS_IN_FLIGHT = 2;
const pendingEventPosts: Array<() => Promise<unknown>> = [];
let eventPostsInFlight = 0;

const pumpEventPosts = () => {
    while (eventPostsInFlight < MAX_EVENT_POSTS_IN_FLIGHT && pendingEventPosts.length > 0) {
        const send = pendingEventPosts.shift()!;
        eventPostsInFlight += 1;
        Promise.resolve()
            .then(send)
            .catch(() => undefined)
            .finally(() => {
                eventPostsInFlight -= 1;
                pumpEventPosts();
            });
    }
};

const enqueueEventPost = (send: () => Promise<unknown>) => {
    pendingEventPosts.push(send);
    pumpEventPosts();
};

declare global {
    interface Window {
        fbq?: (
            command: "init" | "track" | "trackCustom",
            eventName: string,
            parameters?: Record<string, any>,
            additionalParameters?: Record<string, any>
        ) => void;
        dataLayer?: any[];
    }
}

export type TrackEventOptions = {
    skipMeta?: boolean;
    skipGTM?: boolean;
    skipPostHog?: boolean;
};

/**
 * localStorage key of the anonymous device id. It is the PostHog anonymous
 * distinct_id on first visit, the device_id of every backend events-table row,
 * and the `dev_id` handed over between funnels and the app.
 */
export const DEVICE_STORAGE_KEY = "user_uuid";

class Analytics {
    private platform: string = "web";
    private lessonsMap: string = "[]";
    private coursesMap: string = "[]";
    // Explicitly set by the language picker; until then the UI language is
    // resolved lazily from the same detector i18n uses (cookie → device), so
    // events fired before i18n is applied on mount (app_first_launch,
    // splash_screen_visit) still carry the visitor's real language.
    private language: string | null = null;
    private contextProps: Record<string, any> = {};
    private _resolveDeviceId!: () => void;
    readonly deviceIdReady = new Promise<void>((resolve) => {
        this._resolveDeviceId = resolve;
    });

    /** Anonymous device id: `?dev_id` handoff → persisted → freshly generated. */
    private resolveDeviceId(deviceIdFromSearch: string | null): string {
        if (deviceIdFromSearch?.trim()) return deviceIdFromSearch.trim();
        try {
            const stored = localStorage.getItem(DEVICE_STORAGE_KEY);
            if (stored && stored.trim().length > 0) return stored.trim();
        } catch {}
        return uuidv7();
    }

    async init() {
        if (typeof window === "undefined") return;

        const searchParams = new URLSearchParams(window.location.search);
        const deviceId = this.resolveDeviceId(searchParams.get("dev_id"));
        const userIdFromSearch = searchParams.get("user_id")?.trim();

        try {
            localStorage.setItem(DEVICE_STORAGE_KEY, deviceId);
        } catch {}
        this._resolveDeviceId();

        // $feature_flag_called is PostHog's exposure event and is sent by the
        // SDK directly, bypassing trackEvent — mirror it into the backend
        // events table under the historical $exposure shape (flag_key +
        // canonical variant) so those rows continue seamlessly.
        setPostHogExposureMirror((flagKey, rawValue) => {
            const variant = toVariantValue(flagKey, rawValue);
            this.trackToBackend("$exposure", { flag_key: flagKey, ...(variant && { variant }) });
        });

        initPostHog(deviceId);
        if (userIdFromSearch) {
            posthogIdentify(userIdFromSearch);
        }

        const storedLanguage = localStorage.getItem("ljs-lang");
        this.setLanguage(storedLanguage ?? "");
    }

    /**
     * Merge additional contextual properties into every event.
     * Useful for scoping analytics (e.g., experiment flows) without changing all callers.
     */
    setContextProps(props: Record<string, any>) {
        this.contextProps = { ...this.contextProps, ...props };
    }

    /**
     * Remove specific context keys or clear all when keys not provided.
     */
    clearContextProps(keys?: string[]) {
        if (!keys) {
            this.contextProps = {};
            return;
        }
        const next: Record<string, any> = { ...this.contextProps };
        for (const k of keys) delete next[k];
        this.contextProps = next;
    }

    setPlatform(platform: string) {
        this.platform = platform;
    }

    setLanguage(language: string) {
        this.language = typeof language === "string" && language.trim() ? language : null;
    }

    private currentLanguage(): string {
        if (this.language) return this.language;
        if (typeof window === "undefined") return "en";
        try {
            return detectInitialLanguage();
        } catch {
            return "en";
        }
    }

    setLessonsMap(map: unknown) {
        try {
            const serialized = JSON.stringify(map ?? []);
            if (this.lessonsMap !== serialized) {
                this.lessonsMap = serialized;
            }
        } catch {
            this.lessonsMap = "[]";
        }
    }

    setCoursesMap(map: unknown) {
        try {
            const serialized = JSON.stringify(map ?? []);
            if (this.coursesMap !== serialized) {
                this.coursesMap = serialized;
            }
        } catch {
            this.coursesMap = "[]";
        }
    }

    trackGTM(goal: string, params?: Record<string, any>) {
        if (typeof window !== "undefined" && window.dataLayer) {
            let gtmEvent = goal;
            if (goal === "user_email_collected") {
                gtmEvent = "sign_up";
            } else if (goal === "checkout_screen_presented") {
                gtmEvent = "begin_checkout";
            }

            if (goal === "purchase_completed_success" && (params?.price || params?.pricecurrency) && params?.currency) {
                const value = typeof params?.pricecurrency === "number" ? params.pricecurrency : params?.price;
                window.dataLayer.push({ event: "purchase", ...params, currency: params.currency, value });
            } else {
                window.dataLayer.push({ event: gtmEvent, ...params });
            }
        }
    }

    trackEvent(eventName: string, eventProperties?: Record<string, any>, options?: TrackEventOptions) {
        if (typeof window !== "undefined") {
            if (AD_CONSENT_EVENTS.has(eventName)) {
                eventProperties = { ...eventProperties, ...getAdConsent() };
            }

            if (!options?.skipPostHog) {
                posthogCapture(eventName, {
                    ...this.contextProps,
                    ...eventProperties,
                    platform: this.platform,
                    lessonsMap: this.lessonsMap,
                    coursesMap: this.coursesMap,
                    language: this.currentLanguage()
                });
            }

            this.trackToBackend(eventName, eventProperties);

            try {
                this.metaTrackEvent(eventName, eventProperties);
            } catch (e) {
                console.error("Meta track error:", e);
            }

            try {
                this.trackGTM(eventName, eventProperties);
            } catch (e) {
                console.error("GTM track error:", e);
            }
        }
    }

    private async trackToBackend(eventName: string, eventProperties?: Record<string, any>) {
        try {
            await Promise.race([this.deviceIdReady, new Promise((resolve) => setTimeout(resolve, 2000))]);

            const deviceId = localStorage.getItem("user_uuid") || "";
            const clientParams = {
                userAgent: window.navigator.userAgent,
                language: window.navigator.language,
                platform: window.navigator.platform
            };
            const eventParams = {
                ...this.contextProps,
                ...eventProperties,
                platform: this.platform,
                lessonsMap: this.lessonsMap,
                coursesMap: this.coursesMap,
                language: this.currentLanguage()
            };
            const payload: AnalyticsEventPayload = {
                event_name: eventName,
                device_id: deviceId,
                client_params: clientParams,
                event_params: eventParams
            };
            enqueueEventPost(() => CourseDataApi.trackAnalyticsEvent(payload));
        } catch (e) {
            console.error("Backend analytics track error:", e);
        }
    }

    metaTrackEvent(eventName: string, eventProperties?: Record<string, any>) {
        if (typeof window !== "undefined" && typeof window.fbq === "function") {
            // Map special events to Meta Pixel standard events
            let metaEventName = eventName;
            if (eventName === "user_email_collected") {
                metaEventName = "CompleteRegistration";
                window.fbq("track", metaEventName, eventProperties || {});
                return;
            }
            // The Pixel InitiateCheckout is sent from features/shared/meta-events.ts with the
            // predicted value (or without one) — never with the real plan price.
            if (eventName === "checkout_screen_presented") return;
            window.fbq("trackCustom", eventName, eventProperties || {});
        }
    }

    setUserProps(props: Record<string, any>) {
        if (typeof window !== "undefined") {
            posthogSetPersonProperties(props);
        }
    }

    setUserId(userId: number) {
        if (typeof window !== "undefined") {
            // student_id person property backs the "Person display name" project setting
            posthogIdentify(`student_${userId}`, { student_id: `student_${userId}` });
        }
    }

    /**
     * Explicit logout: detach the person, otherwise everything after logout
     * (e.g. walking the funnel again) keeps landing on the old student.
     * PostHog generates the new anonymous id and the backend device id adopts
     * it, so both stay on one identity until the next registration merges it
     * into a person. Callers: explicit logout / profile deletion only — a
     * transient authenticateFx.fail must NOT shred identity.
     */
    reset() {
        if (typeof window === "undefined") return;
        try {
            const newAnonId = posthogReset();
            if (newAnonId) {
                // trackToBackend and the next init read the device id from here
                try {
                    localStorage.setItem(DEVICE_STORAGE_KEY, newAnonId);
                } catch {}
            }
        } catch (e) {
            console.error("Analytics reset error:", e);
        }
    }
}

export const AnalyticsService = new Analytics();
